import { complement, isNil, path, pipe } from "ramda";
import { FC, FunctionComponent, useState } from "react";
import { FormLabel, Icon, Input, InputGroup, InputRightElement, Text } from "@chakra-ui/react";
import { MdError } from "@react-icons/all-files/md/MdError";
import { AiFillCheckCircle } from "@react-icons/all-files/ai/AiFillCheckCircle";

type InputError = {
  code: string;
  message: string;
  type: string;
}

type State = { error: InputError, complete?: boolean };

const inputStyle = {
  style: {
    base: {
      color: "#424770",
      letterSpacing: "0.025em",
      fontFamily: "Roboto, monospace",
      "::placeholder": {
        color: "#aab7c4"
      }
    },
    invalid: {
      color: "#e53e3e",
    }
  }
};

const getError = path(['error', 'message']);

const hasError = pipe(
  getError,
  complement(isNil),
);

const isFieldComplete = path(['complete']);

type Props = {
  label: string
  as: FunctionComponent<any>;
  onChange: () => void;
}

export const CardInput: FC<Props> = ({ label, as, onChange }) => {
  const [state, setState] = useState<State>(undefined);
  const inputHasError = hasError(state);
  const isInputComplete = isFieldComplete(state);

  const checkInput = (event): void => {
    onChange();
    
    const { error, complete } = event;

    setState({
      ...state,
      error: error ? error : undefined,
      complete,
    })
  };

  const getBorderColor = (isHover?: boolean): string => {
    if (isInputComplete && !inputHasError) {
      return isHover ? 'green.500' : 'green.300';
    }

    if (inputHasError) {
      return isHover ? 'red.500' : 'red.300';
    }

    return 'inherit';
  }

  return (
    <>
      <FormLabel>{label}</FormLabel>
      <InputGroup>
        <Input
          as={as}
          py={4}
          options={inputStyle}
          onChange={checkInput}
          borderColor={getBorderColor()}
          _hover={{
            borderColor: getBorderColor(true),
          }}
        />
        {
          isInputComplete && (
            <InputRightElement>
              {inputHasError
                ? <Icon as={MdError} color='red.500' w={5} h={5} />
                : <Icon as={AiFillCheckCircle} color='green.500' w={5} h={5} />
              }
            </InputRightElement>
          )
        }
      </InputGroup>
      {inputHasError && (
        <Text size={"xs"} color={"red.400"} textAlign={"left"} mt={2}>
          {getError(state)}
        </Text>
      )}
    </>
  )
}
